import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Briefcase, Clock, CheckCircle2, XCircle, ArrowRight, User, FileText, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-700',
  reviewing: 'bg-blue-100 text-blue-700',
  accepted: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

const statusIcons = {
  pending: Clock,
  reviewing: Briefcase,
  accepted: CheckCircle2,
  rejected: XCircle,
};

const Dashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [matchedRoles, setMatchedRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_applications: 0,
    pending: 0,
    accepted: 0,
    profile_views: 0,
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (!user.onboarding_completed) {
      navigate('/onboarding');
      return;
    }
    fetchDashboardData();
  }, [user]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [appsRes, matchesRes, statsRes] = await Promise.all([
        axios.get(`${API}/applications`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/matches`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/dashboard/stats`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setApplications(appsRes.data);
      setMatchedRoles(matchesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <Navbar variant="light" />
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-semibold text-gray-900 mb-2">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-gray-500">Track your applications and discover new opportunities.</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <p className="text-gray-500 text-sm mb-1">Applications</p>
            <p className="text-3xl font-semibold text-gray-900">{stats.total_applications}</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <p className="text-gray-500 text-sm mb-1">Pending</p>
            <p className="text-3xl font-semibold text-yellow-600">{stats.pending}</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <p className="text-gray-500 text-sm mb-1">Accepted</p>
            <p className="text-3xl font-semibold text-green-600">{stats.accepted}</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <p className="text-gray-500 text-sm mb-1">Profile Views</p>
            <p className="text-3xl font-semibold text-indigo-600">{stats.profile_views}</p>
          </div>
        </div>

        {/* Content Tabs */}
        <Tabs defaultValue="applications" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="applications">My Applications</TabsTrigger>
            <TabsTrigger value="matches">Matched Roles</TabsTrigger>
          </TabsList>

          <TabsContent value="applications">
            {applications.length > 0 ? (
              <div className="space-y-4">
                {applications.map(app => {
                  const StatusIcon = statusIcons[app.status] || Clock;
                  return (
                    <div key={app.id} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-1">{app.role_title}</h3>
                          <p className="text-gray-500 text-sm">{app.company}</p>
                        </div>
                        <div className="text-right">
                          <Badge className={statusColors[app.status]}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                          </Badge>
                          <p className="text-xs text-gray-400 mt-2">
                            Applied {new Date(app.applied_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white rounded-xl p-12 text-center shadow-sm">
                <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">No applications yet</h3>
                <p className="text-gray-500 mb-6">Start applying to roles that match your profile.</p>
                <Link to="/roles">
                  <Button className="bg-indigo-600 hover:bg-indigo-700">
                    Browse Roles <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            )}
          </TabsContent>

          <TabsContent value="matches">
            {matchedRoles.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {matchedRoles.map(role => (
                  <div
                    key={role.id}
                    onClick={() => navigate(`/roles/${role.id}`)}
                    className="bg-white rounded-xl p-6 cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 shadow-sm"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900">{role.title}</h3>
                        <p className="text-gray-500 text-sm">{role.company}</p>
                      </div>
                      <Badge className="bg-indigo-100 text-indigo-700">
                        {role.match_score}% match
                      </Badge>
                    </div>
                    <p className="text-indigo-600 font-semibold">{role.payRange || role.pay_range}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl p-12 text-center shadow-sm">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">No matches yet</h3>
                <p className="text-gray-500">Complete your profile to get matched with roles.</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Footer />
    </div>
  );
};

export default Dashboard;
